import { Component, Input, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { HttpClient } from "@app/shared/services/http/http-client.service";
import { ToastrService } from "ngx-toastr";
import { SchedulingTeam, Board, SchedulingTeamDriver, SchedulingTeamContact, BillToClient } from "@app/shared/models/Board.model";

@Component({
  selector: "app-client-boards",
  templateUrl: "./client-boards.component.html",
  styleUrls: ["./client-boards.component.css"],
})
export class ClientBoardsComponent implements OnInit, OnChanges {
  @Input() client: any;
  @Input() canEdit: boolean = false;

  schedulingTeams: SchedulingTeam[] = [];
  expandedSchedulingTeamIds: Set<string> = new Set();
  showAddModal: boolean = false;
  showEditModal: boolean = false;
  isLoading: boolean = false;

  newSchedulingTeam: SchedulingTeam = this.createEmptySchedulingTeam();
  editingSchedulingTeam: SchedulingTeam | null = null;

  // Bill to clients for dropdown
  billToClients: BillToClient[] = [];

  selectedBoardNames: string[] = [];
  selectedContacts: { name: string; email?: string; phone?: string; role?: string; doNotContact: boolean }[] = [];
  editSelectedBoardNames: string[] = [];
  editSelectedContacts: { name: string; email?: string; phone?: string; role?: string; doNotContact: boolean }[] = [];

  searchText: string = "";
  filteredSchedulingTeams: SchedulingTeam[] = [];

  // Audit log properties
  auditLogs: any[] = [];
  filteredAuditLogs: any[] = [];
  isAuditLogExpanded: boolean = false;
  isLoadingAuditLogs: boolean = false;
  auditFilters = {
    driverName: '',
    action: '',
    fromDate: '',
    toDate: '',
    schedulingTeam: ''
  };

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    if (this.client?.id) {
      this.loadSchedulingTeams();
      this.loadBillToClients();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["client"] && !changes["client"].firstChange) {
      this.loadSchedulingTeams();
    }
  }

  createEmptySchedulingTeam(): SchedulingTeam {
    return {
      name: "",
      clientId: this.client?.id || "",
      contacts: [],
      boards: [],
      drivers: [],
    };
  }

  loadSchedulingTeams() {
    if (!this.client?.id) return;

    this.isLoading = true;
    this.http.get(`api/board/listByClient/${this.client.id}`).subscribe({
      next: (schedulingTeams: any) => {
        this.schedulingTeams = schedulingTeams || [];
        this.filterSchedulingTeams();
        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error("Error loading scheduling teams");
        console.error(error);
        this.isLoading = false;
      },
    });
  }

  loadBillToClients() {
    this.http.get("api/board/clients").subscribe({
      next: (clients: any) => {
        this.billToClients = clients || [];
      },
      error: (error) => {
        console.error("Error loading clients", error);
      },
    });
  }

  filterSchedulingTeams() {
    if (!this.searchText || this.searchText.trim() === "") {
      this.filteredSchedulingTeams = [...this.schedulingTeams];
      return;
    }

    const searchLower = this.searchText.toLowerCase().trim();
    this.filteredSchedulingTeams = this.schedulingTeams.filter(
      (schedulingTeam) =>
        (schedulingTeam.name && schedulingTeam.name.toLowerCase().includes(searchLower)) ||
        (schedulingTeam.contacts && schedulingTeam.contacts.some(
          (contact) =>
            (contact.name && contact.name.toLowerCase().includes(searchLower)) ||
            (contact.email && contact.email.toLowerCase().includes(searchLower)) ||
            (contact.role && contact.role.toLowerCase().includes(searchLower))
        )) ||
        (schedulingTeam.boards && schedulingTeam.boards.some(
          (board) =>
            board.name && board.name.toLowerCase().includes(searchLower)
        )) ||
        (schedulingTeam.drivers && schedulingTeam.drivers.some(
          (d) =>
            (d.name && d.name.toLowerCase().includes(searchLower)) ||
            (d.lastName && d.lastName.toLowerCase().includes(searchLower)) ||
            (d.driverNo && d.driverNo.toLowerCase().includes(searchLower)) ||
            (d.boardName && d.boardName.toLowerCase().includes(searchLower))
        ))
    );
  }

  toggleSchedulingTeamExpansion(schedulingTeamId: string) {
    if (this.expandedSchedulingTeamIds.has(schedulingTeamId)) {
      this.expandedSchedulingTeamIds.delete(schedulingTeamId);
    } else {
      this.expandedSchedulingTeamIds.add(schedulingTeamId);
    }
  }

  isSchedulingTeamExpanded(schedulingTeamId: string): boolean {
    return this.expandedSchedulingTeamIds.has(schedulingTeamId);
  }

  openAddModal() {
    this.newSchedulingTeam = this.createEmptySchedulingTeam();
    this.selectedBoardNames = [""]; // Start with one empty board entry
    this.selectedContacts = [];
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
    this.newSchedulingTeam = this.createEmptySchedulingTeam();
    this.selectedBoardNames = [];
    this.selectedContacts = [];
  }

  openEditModal(schedulingTeam: SchedulingTeam) {
    this.editingSchedulingTeam = { ...schedulingTeam };
    this.editSelectedBoardNames = schedulingTeam.boards?.map((board) => board.name) || [];
    this.editSelectedContacts = (schedulingTeam.contacts || []).map((c) => ({
      name: c.name || '',
      email: c.email || '',
      phone: c.phone || '',
      role: c.role || '',
      doNotContact: c.doNotContact || false
    }));
    
    // If no contacts exist, add one empty contact field to start with
    if (this.editSelectedContacts.length === 0) {
      this.editSelectedContacts.push({ name: "", email: "", phone: "", role: "", doNotContact: false });
    }
    
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingSchedulingTeam = null;
    this.editSelectedBoardNames = [];
    this.editSelectedContacts = [];
  }

  createSchedulingTeam() {
    if (!this.validateNewSchedulingTeam()) return;

    // Filter out empty entries
    const validBoardNames = this.selectedBoardNames.filter((name) => name && name.trim());
    const validContacts = this.selectedContacts.filter((c) => c.name && c.name.trim());

    const request = {
      name: this.newSchedulingTeam.name,
      clientId: this.client.id,
      boardNames: validBoardNames,
      contacts: validContacts,
      driverAssignments: [], // Drivers are no longer assigned from this page
    };

    this.http.post("api/board/create", request).subscribe({
      next: () => {
        this.toastr.success("Scheduling team created successfully");
        this.closeAddModal();
        this.loadSchedulingTeams();
      },
      error: (error) => {
        this.toastr.error(error || "Error creating scheduling team");
        console.error(error);
      },
    });
  }

  updateSchedulingTeam() {
    if (!this.editingSchedulingTeam || !this.validateEditSchedulingTeam()) return;

    // Filter out empty entries
    const validBoardNames = this.editSelectedBoardNames.filter((name) => name && name.trim());
    const validContacts = this.editSelectedContacts.filter((c) => c.name && c.name.trim());

    // Keep existing driver assignments (map to the format expected by the API)
    const existingDriverAssignments = this.editingSchedulingTeam.drivers.map((d) => ({
      driverId: d.driverId,
      boardName: d.boardName,
      billToClientId: d.billToClientId
    }));

    const request = {
      id: this.editingSchedulingTeam.id,
      name: this.editingSchedulingTeam.name,
      boardNames: validBoardNames,
      contacts: validContacts,
      driverAssignments: existingDriverAssignments,
    };

    this.http.patch("api/board/update", request).subscribe({
      next: () => {
        this.toastr.success("Scheduling team updated successfully");
        this.closeEditModal();
        this.loadSchedulingTeams();
      },
      error: (error) => {
        this.toastr.error(error || "Error updating scheduling team");
        console.error(error);
      },
    });
  }

  deleteSchedulingTeam(schedulingTeam: SchedulingTeam) {
    if (!confirm(`Are you sure you want to delete the scheduling team "${schedulingTeam.name}"?`)) return;

    this.http.delete(`api/board/delete/${schedulingTeam.id}`).subscribe({
      next: () => {
        this.toastr.success("Scheduling team deleted successfully");
        this.loadSchedulingTeams();
      },
      error: (error) => {
        this.toastr.error(error || "Error deleting scheduling team");
        console.error(error);
      },
    });
  }

  validateNewSchedulingTeam(): boolean {
    if (!this.newSchedulingTeam.name || this.newSchedulingTeam.name.trim() === "") {
      this.toastr.warning("Scheduling team name is required");
      return false;
    }
    return true;
  }

  validateEditSchedulingTeam(): boolean {
    if (!this.editingSchedulingTeam) return false;

    if (!this.editingSchedulingTeam.name || this.editingSchedulingTeam.name.trim() === "") {
      this.toastr.warning("Scheduling team name is required");
      return false;
    }
    return true;
  }

  // Helper methods for managing board names (Add Modal)
  addNewBoard() {
    this.selectedBoardNames.push("");
  }

  removeNewBoard(index: number) {
    if (this.selectedBoardNames.length > 0) {
      this.selectedBoardNames.splice(index, 1);
    }
  }

  // Helper methods for managing contacts (Add Modal)
  addNewContact() {
    this.selectedContacts.push({ name: "", email: "", phone: "", role: "", doNotContact: false });
  }

  removeNewContact(index: number) {
    this.selectedContacts.splice(index, 1);
  }

  // Helper methods for managing board names (Edit Modal)
  addEditBoard() {
    this.editSelectedBoardNames.push("");
  }

  removeEditBoard(index: number) {
    if (this.editSelectedBoardNames.length > 0) {
      this.editSelectedBoardNames.splice(index, 1);
    }
  }

  // Helper methods for managing contacts (Edit Modal)
  addEditContact() {
    this.editSelectedContacts.push({ name: "", email: "", phone: "", role: "", doNotContact: false });
  }

  removeEditContact(index: number) {
    this.editSelectedContacts.splice(index, 1);
  }

  highlightText(text: string): string {
    if (!text) return "";
    if (!this.searchText || this.searchText.trim() === "") return text;

    const searchTerm = this.searchText.trim();
    const regex = new RegExp(`(${this.escapeRegex(searchTerm)})`, "gi");
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // TrackBy functions to prevent input focus loss
  trackByIndex(index: number, item: any): number {
    return index;
  }

  // Audit log methods
  toggleAuditLogExpansion() {
    this.isAuditLogExpanded = !this.isAuditLogExpanded;
    
    // Lazy load audit logs when expanded for the first time
    if (this.isAuditLogExpanded && this.auditLogs.length === 0 && !this.isLoadingAuditLogs) {
      this.loadAuditLogs();
    }
  }

  loadAuditLogs() {
    if (!this.client?.id) return;

    this.isLoadingAuditLogs = true;
    this.http.get(`api/board/audit-logs/${this.client.id}`).subscribe({
      next: (logs: any) => {
        this.auditLogs = logs || [];
        this.filterAuditLogs(); // Apply any existing filters
        this.isLoadingAuditLogs = false;
      },
      error: (error) => {
        this.toastr.error("Error loading driver assignment changes");
        console.error(error);
        this.isLoadingAuditLogs = false;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  filterAuditLogs() {
    this.filteredAuditLogs = this.auditLogs.filter(log => {
      // Driver name filter
      if (this.auditFilters.driverName && this.auditFilters.driverName.trim()) {
        const driverNameMatch = (log.driverName || '').toLowerCase().includes(
          this.auditFilters.driverName.toLowerCase().trim()
        );
        if (!driverNameMatch) return false;
      }

      // Action filter
      if (this.auditFilters.action && log.action !== this.auditFilters.action) {
        return false;
      }

      // Date range filter
      if (this.auditFilters.fromDate || this.auditFilters.toDate) {
        const logDate = new Date(log.dateCreatedUtc);
        logDate.setHours(0, 0, 0, 0); // Reset time for date-only comparison
        
        if (this.auditFilters.fromDate) {
          const fromDate = new Date(this.auditFilters.fromDate);
          if (logDate < fromDate) return false;
        }
        
        if (this.auditFilters.toDate) {
          const toDate = new Date(this.auditFilters.toDate);
          if (logDate > toDate) return false;
        }
      }

      // Scheduling team filter
      if (this.auditFilters.schedulingTeam && this.auditFilters.schedulingTeam.trim()) {
        const teamNameMatch = (log.schedulingTeamName || '').toLowerCase().includes(
          this.auditFilters.schedulingTeam.toLowerCase().trim()
        );
        if (!teamNameMatch) return false;
      }

      return true;
    });
  }

  clearAuditFilters() {
    this.auditFilters = {
      driverName: '',
      action: '',
      fromDate: '',
      toDate: '',
      schedulingTeam: ''
    };
    this.filterAuditLogs();
  }

  exportAuditLogsToCSV() {
    if (this.filteredAuditLogs.length === 0) {
      this.toastr.warning('No data to export');
      return;
    }

    const headers = [
      'Date',
      'Driver Name',
      'Action',
      'Scheduling Team',
      'Board',
      'Bill To Client',
      'User'
    ];

    const csvData = this.filteredAuditLogs.map(log => [
      this.formatDate(log.dateCreatedUtc),
      log.driverName || 'Unknown Driver',
      log.action,
      log.schedulingTeamName || '',
      log.boardName || '',
      log.billToClientName || '',
      log.userName || ''
    ]);

    // Add headers to the beginning
    csvData.unshift(headers);

    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(field => {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        const fieldStr = String(field || '');
        if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
          return '"' + fieldStr.replace(/"/g, '""') + '"';
        }
        return fieldStr;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const clientName = this.client?.companyName || 'Client';
    const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    link.setAttribute('download', `${clientName}_Driver_Assignment_Changes_${timestamp}.csv`);
    
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    this.toastr.success('Audit log exported successfully');
  }
}
